/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { LitElement, html } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import { FOOTER_SIZE } from './defs';
import styles from './footer.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

export { FOOTER_SIZE };

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * The top-level element in footer.
 *
 * @element c4d-footer
 * @csspart main-section - The <section> container. Usage: `c4d-footer::part(main-section)`
 * @csspart main-container - The main container. Usage: `c4d-footer::part(main-container)`
 * @csspart logo-container - The logo container. Usage: `c4d-footer::part(logo-container)`
 * @csspart logo-row - The logo row. Usage: `c4d-footer::part(logo-row)`
 * @slot brand - The brand content.
 * @slot legal-nav - The legal nav content.
 */

/**
 * @deprecated Locale button is deprecated and no longer rendered.
 * This slot is kept temporarily for backward compatibility.
 * @slot locale-button - The locale button content.
 */
@customElement(`${c4dPrefix}-footer`)
class C4DFooter extends StableSelectorMixin(LitElement) {
  /**
   * Footer size.
   */
  @property({ reflect: true })
  size = FOOTER_SIZE.REGULAR;

  /**
   * @deprecated Locale button is deprecated and no longer rendered.
   * This property will be removed in a future major release.
   */
  @property({ type: Boolean, attribute: 'disable-locale-button' })
  disableLocaleButton = false;

  connectedCallback() {
    this.setAttribute('role', 'contentinfo');

    if (!this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', 'footer');
    }
    super.connectedCallback();
  }

  render() {
    const { disableLocaleButton } = this;

    const classes = {
      [`${prefix}--footer__logo-container`]: true,
      [`${prefix}--footer__locale-button--disabled`]: disableLocaleButton,
    };

    return html`
      <section part="main-section" class="${prefix}--footer__main">
        <div part="main-container" class="${prefix}--footer__main-container">
          <div part="logo-container" class=${classMap(classes)}>
            <div part="logo-row" class="${prefix}--footer__logo-row">
              <slot name="brand"></slot>
              <!-- Locale button deprecated: intentionally -->
              <!-- <slot name="locale-button"></slot> -->
              <!-- <slot name="language-selector"></slot> -->
              <slot></slot>
            </div>
          </div>
        </div>
      </section>
      <slot name="legal-nav"></slot>
    `;
  }

  updated() {
    if (this.querySelector('[slot="locale-button"]')) {
      console.warn(
        '<c4d-footer>: locale-button slot is deprecated and will be removed.'
      );
    }
  }

  static get stableSelector() {
    return `${c4dPrefix}--footer`;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

export default C4DFooter;
